import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, eventQuery } from "../api";
import type { Session, SessionWhatsappGroupData } from "../types";
import { Modal } from "./Modal";
import { WhatsappIcon } from "./WhatsappIcon";
import { IconActionButton } from "./AdminActions";

export function SessionWhatsappGroupModal({ eventId, session, onClose, onChanged, onToast }: {
  eventId: number;
  session: Session;
  onClose: () => void;
  onChanged: () => Promise<void>;
  onToast: (message: string) => void;
}) {
  const [data, setData] = useState<SessionWhatsappGroupData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [manualInviteUrl, setManualInviteUrl] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const result = await api<SessionWhatsappGroupData>(`/api/sessions/${session.id}/whatsapp-group?${eventQuery(eventId)}`);
      setData(result);
      return result;
    } catch (loadError) {
      setLoadError(loadError instanceof Error ? loadError.message : "No se pudo cargar el grupo de WhatsApp");
      throw loadError;
    } finally {
      setLoading(false);
    }
  }, [eventId, session.id]);

  useEffect(() => { load().catch(() => undefined); }, [load]);
  useEffect(() => {
    if (!data?.summary.pending) return;
    const timer = window.setInterval(() => load().catch(() => undefined), 8000);
    return () => window.clearInterval(timer);
  }, [data?.summary.pending, load]);

  async function createGroup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      const result = await api<SessionWhatsappGroupData>(`/api/sessions/${session.id}/whatsapp-group`, { method: "POST", body: JSON.stringify({ eventId, title: form.get("title") }) });
      setData(result);
      await onChanged();
      onToast("Grupo creado; las invitaciones quedaron programadas");
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "No se pudo crear el grupo");
    } finally {
      setBusy(false);
    }
  }

  async function retryErrors() {
    setBusy(true);
    setError("");
    try {
      setData(await api<SessionWhatsappGroupData>(`/api/sessions/${session.id}/whatsapp-group/retry-errors`, { method: "POST", body: JSON.stringify({ eventId }) }));
      onToast("Los envíos fallidos volvieron a la cola");
    } catch (retryError) {
      setError(retryError instanceof Error ? retryError.message : "No se pudieron reintentar los envíos");
    } finally {
      setBusy(false);
    }
  }

  async function resendMissing() {
    setBusy(true);
    setError("");
    try {
      const result = await api<SessionWhatsappGroupData>(`/api/sessions/${session.id}/whatsapp-group/resend-missing`, { method: "POST", body: JSON.stringify({ eventId }) });
      setData(result);
      await onChanged();
      onToast(result.resend?.queued ? `${result.resend.queued} invitaciones quedaron programadas; ${result.resend.alreadyJoined} integrantes ya están en el grupo` : "Todos los integrantes ya están dentro del grupo");
    } catch (resendError) {
      setError(resendError instanceof Error ? resendError.message : "No se pudieron reenviar las invitaciones");
    } finally { setBusy(false); }
  }

  async function recoverInviteLink() {
    setBusy(true);
    setError("");
    try {
      const result = await api<SessionWhatsappGroupData>(`/api/sessions/${session.id}/whatsapp-group`, { method: "POST", body: JSON.stringify({ eventId, title: data?.group?.title || session.title, inviteUrl: manualInviteUrl.trim() || undefined }) });
      setData(result);
      await onChanged();
      onToast(`${manualInviteUrl.trim() ? "Enlace guardado" : "Enlace recuperado"}; las invitaciones quedaron programadas`);
    } catch (recoverError) {
      setError(recoverError instanceof Error ? recoverError.message : "No se pudo recuperar el enlace del grupo");
    } finally { setBusy(false); }
  }

  const existingGroup = Boolean(data?.group?.whatsappGroupId);

  return <Modal title="Grupo de WhatsApp de la sesión" eyebrow="Invitaciones escalonadas" wide onClose={onClose}>
    {!data ? loading ? <div className="skeleton page-skeleton short" /> : <div className="group-load-error" role="alert">
      <span className="group-whatsapp-mark"><WhatsappIcon /></span>
      <div><strong>No se pudo abrir el grupo de WhatsApp</strong><p>{loadError || "La consulta no devolvió información."}</p></div>
      <IconActionButton label="Reintentar" icon="refresh" onClick={() => load().catch(() => undefined)} />
    </div> : !existingGroup ? <form onSubmit={createGroup}>
      <div className="group-create-intro"><span className="group-whatsapp-mark"><WhatsappIcon /></span><div><h3>{session.title}</h3><p>Se creará un grupo administrado por la cuenta de WhatsApp vinculada. Cada integrante recibirá individualmente el enlace para unirse.</p></div></div>
      {error && <div className="form-error">{error}</div>}
      {data.summary.missingPhone.length > 0 && <div className="group-warning"><strong>Faltan teléfonos</strong><span>{data.summary.missingPhone.join(", ")}</span></div>}
      <label className="field"><span>Nombre del grupo *</span><input name="title" required maxLength={100} defaultValue={session.title} /></label>
      <div className="protected-communication"><span>▣</span><div><strong>Comunicación protegida: INVITAR A GRUPO</strong><p>Incluye el nombre del faculty, la sesión y el enlace. Los envíos no se pueden anular y se separan aleatoriamente entre 1 y 2 minutos.</p></div></div>
      <div className="modal-footer"><IconActionButton label="Cancelar" icon="cancel" onClick={onClose} /><IconActionButton label={data.whatsapp.status !== "ready" ? "WhatsApp no vinculado" : data.group?.whatsappGroupId ? `Recuperar e invitar a ${data.summary.total}` : `Crear e invitar a ${data.summary.total}`} icon="send" tone="success" type="submit" busy={busy} disabled={busy || data.whatsapp.status !== "ready" || Boolean(data.summary.missingPhone.length)} /></div>
    </form> : <div className="group-status-view">
      {error && <div className="form-error">{error}</div>}
      <section className="group-summary"><div className="group-whatsapp-mark"><WhatsappIcon /></div><div><span className="eyebrow">Grupo ya creado</span><h3>{data.group!.title}</h3><p>{data.group!.inviteUrl ? "Este grupo ya existe. Podés abrirlo o reenviar la invitación únicamente a quienes todavía no se unieron." : "Este grupo ya existe en WhatsApp. Sólo falta recuperar su enlace de invitación; no se creará otro grupo."}</p>{data.group!.inviteUrl && <a href={data.group!.inviteUrl} target="_blank" rel="noreferrer">Abrir enlace de invitación ↗</a>}</div><div className="group-progress"><strong>{data.summary.sent}/{data.summary.total}</strong><span>invitaciones</span></div></section>
      {!data.group!.inviteUrl && <div className="group-warning group-link-recovery"><strong>Enlace pendiente</strong><span>Probá primero la recuperación automática. Si WhatsApp vuelve a rechazarla, copiá el enlace desde “Info. del grupo → Enlace de invitación” y pegalo aquí.</span><input type="url" value={manualInviteUrl} onChange={(event) => setManualInviteUrl(event.target.value)} placeholder="https://chat.whatsapp.com/…" aria-label="Enlace de invitación del grupo" /></div>}
      <div className="protected-communication compact"><span>▣</span><div><strong>Cola no anulable</strong><p>Los pendientes continuarán enviándose aun si se reinicia el servidor.</p></div></div>
      <section className="group-invitation-list"><header><strong>Destinatarios</strong><span>{data.summary.pending} pendientes · {data.summary.error} con error</span></header>{data.invitations.length ? data.invitations.map((invitation) => <article key={invitation.id}><span className={`group-delivery-state ${invitation.status}`}>{statusGlyph(invitation.status)}</span><div><strong>{invitation.recipientName}</strong><small>{invitation.recipientPhone}</small></div><div><span>{statusLabel(invitation.status)}</span><small>{invitation.status === "sent" ? formatTime(invitation.sentAt) : invitation.status === "error" ? invitation.errorMessage : `Programado ${formatTime(invitation.scheduledAt)}`}</small></div></article>) : data.participants.map((participant) => <article key={participant.id}><span className="group-delivery-state">⋯</span><div><strong>{participant.name}</strong><small>{participant.phone || "Sin WhatsApp declarado"}</small></div><div><span>Esperando enlace</span><small>Se programará al recuperar el enlace</small></div></article>)}</section>
      <div className="modal-footer"><IconActionButton label="Cerrar" icon="close" onClick={onClose} />{data.group!.inviteUrl && data.summary.error > 0 && <IconActionButton label="Reintentar errores" icon="refresh" busy={busy} disabled={busy} onClick={retryErrors} />}{data.group!.inviteUrl ? <IconActionButton label="Reenviar a quienes aún no se unieron" icon="send" tone="success" busy={busy} disabled={busy || data.whatsapp.status !== "ready"} onClick={resendMissing} /> : <IconActionButton label={manualInviteUrl.trim() ? "Guardar enlace e invitar" : "Recuperar enlace del grupo"} icon={manualInviteUrl.trim() ? "send" : "refresh"} tone="success" busy={busy} disabled={busy || data.whatsapp.status !== "ready"} onClick={recoverInviteLink} />}</div>
    </div>}
  </Modal>;
}

function statusGlyph(status: string) { return status === "sent" ? "✓" : status === "error" ? "!" : "⋯"; }
function statusLabel(status: string) { return status === "sent" ? "Enviada" : status === "error" ? "Error" : status === "sending" ? "Enviando" : "En espera"; }
function formatTime(value: string | null) { return value ? new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(value)) : ""; }
